import React, { useMemo, useState } from 'react';
import { Chart } from 'primereact/chart';
import { InputSwitch } from 'primereact/inputswitch';
import { useSelector, useDispatch } from 'react-redux';
import { loadResidenceCombinationsAsync, selectResidenceCombinations } from '../dashboardSlice';
import { ChartCard } from './ChartCard';
import { useThemeVersion } from '../../../useTheme';

// R1/R2/R3 é uma progressão (ano de residência), não categorias soltas —
// rampa sequencial (claro→escuro) em vez de cores arbitrárias por fatia.
const RESIDENCE_COLORS = { R1: '#90CAF9', R2: '#42A5F5', R3: '#1565C0' };
const FALLBACK_COLOR = '#9E9E9E';

export const ResidenceCombinationsChart = () => {
    const dispatch = useDispatch();
    const { data, status, error } = useSelector(selectResidenceCombinations);
    // getComputedStyle abaixo só lê o CSS de novo se essa memo recalcular —
    // sem isso, trocar de tema depois de montado deixa a cor antiga presa.
    const themeVersion = useThemeVersion();
    // Sem useEffect de montagem aqui de propósito — a carga inicial já vem
    // do TAB_LOADERS da aba Residência (DashboardView.js); disparar de novo
    // ao montar duplicaria a mesma requisição (mesmo problema já corrigido
    // na aba Atividade). Só busca de novo quando o toggle muda de verdade.
    const [activeOnly, setActiveOnly] = useState(true);

    const onToggleActiveOnly = (e) => {
        setActiveOnly(e.value);
        dispatch(loadResidenceCombinationsAsync(e.value));
    };

    const chartData = useMemo(() => {
        // Canvas não entende var(...) — precisa do valor já resolvido.
        const surfaceColor = getComputedStyle(document.documentElement).getPropertyValue('--surface-card').trim() || '#ffffff';

        if (!Array.isArray(data) || !data.length) {
            return { labels: [], datasets: [{ data: [], backgroundColor: [] }] };
        }

        const sortedData = [...data].sort((a, b) => b.userCount - a.userCount);

        return {
            labels: sortedData.map(item => item.residences),
            datasets: [{
                label: 'Usuários',
                data: sortedData.map(item => item.userCount),
                backgroundColor: sortedData.map(item => RESIDENCE_COLORS[item.residences] || FALLBACK_COLOR),
                borderColor: surfaceColor,
                borderWidth: 2
            }]
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, themeVersion]);

    const chartOptions = useMemo(() => ({
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--text-color')
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
                        const percentage = total > 0 ? ((value * 100) / total).toFixed(1) : 0;
                        return `${context.label}: ${value} usuários (${percentage}%)`;
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [themeVersion]);

    return (
        <ChartCard
            title="Usuários por residência"
            loading={status === 'loading'}
            error={error}
            onReload={() => dispatch(loadResidenceCombinationsAsync(activeOnly))}
            headerExtra={(
                <InputSwitch
                    inputId="residence-active-only"
                    checked={activeOnly}
                    onChange={onToggleActiveOnly}
                    disabled={status === 'loading'}
                    tooltip="Somente ativos"
                    tooltipOptions={{ position: 'top' }}
                    className="mr-2"
                />
            )}
        >
            <Chart type="pie" data={chartData} options={chartOptions} style={{ height: '280px', maxWidth: '400px', margin: '0 auto' }} />
        </ChartCard>
    );
};
